import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';

/**
 * Playback-speed picker, rendered INLINE on purpose.
 *
 * Every portalling primitive in @mlm/ui (DropdownMenu, Select) mounts into
 * `document.body`, which is outside the fullscreened player element — the menu
 * would simply not exist for a learner in fullscreen. This renders as a child
 * of the player wrapper instead, so it survives all three fullscreen modes.
 */
export function SpeedMenu({
  rate,
  rates,
  onChange,
  disabled,
}: {
  rate: number;
  rates: number[];
  onChange: (rate: number) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  if (rates.length === 0) return null;

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label="Playback speed"
        aria-haspopup="true"
        aria-expanded={open}
        title="Playback speed"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 min-w-8 items-center justify-center rounded px-1.5 text-xs font-semibold tabular-nums text-white/90 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
      >
        {rate}&times;
      </button>
      {open && (
        <div
          role="radiogroup"
          aria-label="Playback speed"
          className="absolute bottom-full right-0 mb-2 flex flex-col overflow-hidden rounded-md bg-black/85 py-1 text-xs shadow-lg"
        >
          {rates.map((option) => {
            const active = option === rate;
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={active}
                // Roving tabindex: the menu is one tab stop, not six, so a
                // keyboard learner is not trapped walking past every speed.
                tabIndex={active ? 0 : -1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={cn(
                  'px-4 py-1.5 text-left tabular-nums hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active ? 'font-semibold text-white' : 'text-white/75',
                )}
              >
                {option === 1 ? 'Normal' : `${option}×`}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
